import { useState, useEffect, useMemo, useCallback } from 'react';
import { getPatrolSummaries, PatrolSummary, getCheckpointStats } from 'api/activityReport';

export interface PatrolStats {
  patrolCount: number;
  incidentCount: number;
  clearCount: number;
  warningCount: number;
  emergencyCount: number;
  incompleteCount: number;
}

export interface CheckpointStats {
  total: number;
  info: number;
  clear: number;
  warning: number;
  emergency: number;
  incomplete: number;
}

export interface DurationStats {
  totalHours: number;
  totalMinutes: number;
  avgPatrolLength: number;
  avgPatrolsPerDay: number;
}

export interface TransformedPatrol {
  id: number;
  scheduleAssignmentId: number;
  guardName: string;
  startTime: string;
  endTime: string;
  status: 'clear' | 'warning' | 'emergency' | 'incomplete';
  patrolType: 'Static' | 'Mobile';
  hasChecklist: boolean;
  hasIncident: boolean;
  hasAttachment: boolean;
  comment: string;
  attachments: { id: string; name: string; type: string }[];
  checklists: any[]; // Loaded on demand via getPatrolDetails
}

const mapActivityTitle = (title: string, isComplete?: boolean): 'clear' | 'warning' | 'emergency' | 'incomplete' => {
  switch (title?.toLowerCase()) {
    case 'clear':
    case 'complete':
      return 'clear';
    case 'attention':
      return 'warning';
    case 'urgent':
      return 'emergency';
    default:
      // Patrol was ended (end_date > start_date) but activity_title wasn't
      // set to a recognized status — treat as complete rather than incomplete.
      // This handles patrols completed from the mobile app where the activity
      // lookup may not find a matching title under the same deliverable.
      if (isComplete) return 'clear';
      return 'incomplete';
  }
};

// Transform patrol summaries (no checklist data - loaded on demand)
const transformPatrolSummaries = (data: PatrolSummary[]): {
  patrols: TransformedPatrol[];
  patrolStats: PatrolStats;
  durationStats: DurationStats;
} => {
  const patrolsMap = new Map<number, TransformedPatrol>();
  let incidentCount = 0;
  let clearCount = 0;
  let warningCount = 0;
  let emergencyCount = 0;
  let incompleteCount = 0;
  let totalDurationMs = 0;
  const dailyPatrols: { [key: string]: number } = {};

  data.forEach((patrol) => {
    const patrolId = patrol.comment_list_id;

    if (!patrolsMap.has(patrolId)) {
      const status = mapActivityTitle(patrol.activity_title, patrol.complete === 'true');

      // Count status
      if (status === 'clear') clearCount++;
      else if (status === 'warning') warningCount++;
      else if (status === 'emergency') emergencyCount++;
      else incompleteCount++;

      // Count incidents from isIncident flag
      if (patrol.isIncident === 'true') {
        incidentCount++;
      }

      // Calculate duration
      const startDate = new Date(patrol.start_date);
      const endDate = new Date(patrol.end_date);
      const duration = endDate.getTime() - startDate.getTime();
      totalDurationMs += duration;

      // Track daily patrols (normalize to local date, not UTC)
      const localDate = new Date(startDate);
      localDate.setHours(0, 0, 0, 0);
      const dayKey = localDate.toISOString();
      dailyPatrols[dayKey] = (dailyPatrols[dayKey] || 0) + 1;

      // Parse file attachments
      const vvFiles = patrol.vv_file_id ? patrol.vv_file_id.split(',').filter(Boolean) : [];
      const ddsFiles = patrol.dds_file_id ? patrol.dds_file_id.split(',').filter(Boolean) : [];
      const attachments = [
        ...vvFiles.map((id, idx) => ({ id, name: `File-${idx + 1}`, type: 'vv' })),
        ...ddsFiles.map((id, idx) => ({ id, name: `DDS-${idx + 1}`, type: 'dds' })),
      ];

      patrolsMap.set(patrolId, {
        id: patrolId,
        scheduleAssignmentId: patrol.schedule_assignment_id,
        guardName: patrol.full_name,
        startTime: patrol.start_date,
        endTime: patrol.end_date,
        status,
        patrolType: patrol.PatrolType || 'Mobile',
        hasChecklist: patrol.hasChecklist || false,
        hasIncident: patrol.isIncident === 'true',
        hasAttachment: attachments.length > 0,
        comment: patrol.comment || '',
        attachments,
        checklists: [], // Loaded on demand via getPatrolDetails
      });
    }
  });

  const patrols = Array.from(patrolsMap.values());
  const patrolCount = patrols.length;
  const totalDays = Object.keys(dailyPatrols).length || 1;
  const totalMinutes = Math.floor(totalDurationMs / 60000);
  const avgPatrolLength = patrolCount > 0 ? Math.round(totalMinutes / patrolCount) : 0;

  return {
    patrols,
    patrolStats: {
      patrolCount,
      incidentCount,
      clearCount,
      warningCount,
      emergencyCount,
      incompleteCount,
    },
    durationStats: {
      totalHours: Math.floor(totalMinutes / 60),
      totalMinutes: totalMinutes % 60,
      avgPatrolLength,
      avgPatrolsPerDay: parseFloat((patrolCount / totalDays).toFixed(2)),
    },
  };
};

export interface UseActivityReportParams {
  customerId: string | number | null;
  addressId: string | number | null;
  startDate: Date | null;
  endDate: Date | null;
}

export const useActivityReport = ({
  customerId,
  addressId,
  startDate,
  endDate,
}: UseActivityReportParams) => {
  const [rawData, setRawData] = useState<PatrolSummary[]>([]);
  const [checkpointStats, setCheckpointStats] = useState<CheckpointStats>({
    total: 0,
    info: 0,
    clear: 0,
    warning: 0,
    emergency: 0,
    incomplete: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchCounter, setRefetchCounter] = useState(0);

  // Fetch patrol summaries and checkpoint stats in parallel
  useEffect(() => {
    let isCurrent = true;

    // Clear data when parameters become invalid
    if (!customerId || !addressId || !startDate || !endDate) {
      setRawData([]);
      setCheckpointStats({ total: 0, info: 0, clear: 0, warning: 0, emergency: 0, incomplete: 0 });
      setError(null);
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setRawData([]);
      setIsLoading(true);
      setError(null);

      const params = {
        CustomerId: customerId,
        AddressId: addressId,
        FromDate: startDate.toISOString(),
        ToDate: endDate.toISOString(),
      };

      try {
        // Fetch patrol summaries and checkpoint stats in parallel
        const [patrolData, statsData] = await Promise.all([
          getPatrolSummaries(params),
          getCheckpointStats(params),
        ]);

        if (isCurrent) {
          setRawData(patrolData);
          setCheckpointStats(statsData);
        }
      } catch (err: any) {
        if (isCurrent) {
          console.error('Error fetching activity report:', err);
          setError(err.message || 'Failed to fetch activity report');
          setRawData([]);
        }
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isCurrent = false;
    };
  }, [customerId, addressId, startDate, endDate, refetchCounter]);

  const refetch = useCallback(() => {
    setRefetchCounter((c) => c + 1);
  }, []);

  const { patrols, patrolStats, durationStats } = useMemo(
    () => transformPatrolSummaries(rawData),
    [rawData]
  );

  return {
    patrols,
    patrolStats,
    checkpointStats,
    durationStats,
    isLoading,
    error,
    refetch,
  };
};

export default useActivityReport;
